import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AddRoutingTargetsBody,
  CaseRoutingView,
  DispatchSubmissionBody,
  LenderDecisionListResponse,
  LenderDecisionView,
  LenderSubmissionListResponse,
  LenderSubmissionView,
  OfferView,
  PackageExportView,
  PackageReadinessView,
  PrepareRoutingBody,
  PrepareSubmissionBody,
  ReconcileSubmissionBody,
  RemoveRoutingTargetBody,
  RoutingPrepareResponse,
  RecordDecisionBody,
  RecordDecisionResponse,
  RecordOfferDisclosureBody,
  SubmissionPackageListResponse,
  SubmissionPackageView,
  SubmissionReadinessView,
  TexOffersResponse,
  WithdrawSubmissionBody,
} from '@tex/finance-shared'
import { apiGet, apiSend } from './api'
import { caseKeys } from './cases'

const IDEMPOTENCY_HEADER = 'Idempotency-Key'
const cases = '/api/v1/cases'

/**
 * Query keys for the M3 lender surface (docs/PLAN.md §4 PR 11–13). Every write below invalidates
 * the sibling lists and the case itself: packages feed submissions, submissions move the stage,
 * decisions produce offers, and offers/disclosure change what the buyer may see.
 */
export const lenderKeys = {
  packages: (caseId: string) => ['cases', caseId, 'packages'] as const,
  packageReadiness: (caseId: string, programVersionId: string) => ['cases', caseId, 'packages', 'readiness', programVersionId] as const,
  submissions: (caseId: string) => ['cases', caseId, 'submissions'] as const,
  submissionReadiness: (caseId: string, submissionId: string) => ['cases', caseId, 'submissions', submissionId, 'readiness'] as const,
  decisions: (caseId: string) => ['cases', caseId, 'decisions'] as const,
  offers: (caseId: string) => ['cases', caseId, 'offers'] as const,
  routing: (caseId: string) => ['cases', caseId, 'routing'] as const,
}

function useInvalidateLenderState(caseId: string) {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: lenderKeys.packages(caseId) }),
      qc.invalidateQueries({ queryKey: lenderKeys.submissions(caseId) }),
      qc.invalidateQueries({ queryKey: lenderKeys.decisions(caseId) }),
      qc.invalidateQueries({ queryKey: lenderKeys.offers(caseId) }),
      qc.invalidateQueries({ queryKey: lenderKeys.routing(caseId) }),
      qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
      qc.invalidateQueries({ queryKey: ['work-queue'] }),
    ])
}

// ── packages (PR 11) ──

export function usePackages(caseId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.packages(caseId),
    enabled,
    queryFn: () => apiGet<SubmissionPackageListResponse>(`${cases}/${caseId}/packages`),
  })
}

/** What a build for `programVersionId` would contain right now and everything stopping it. */
export function usePackageReadiness(caseId: string, programVersionId: string | null) {
  return useQuery({
    queryKey: lenderKeys.packageReadiness(caseId, programVersionId ?? ''),
    enabled: programVersionId !== null,
    queryFn: () =>
      apiGet<PackageReadinessView>(`${cases}/${caseId}/packages/readiness?program_version_id=${encodeURIComponent(programVersionId ?? '')}`),
  })
}

/** Named command `buildPackage`; the server refuses an identical rebuild with 409 `package_unchanged`. */
export function useBuildPackage(caseId: string, idempotencyKey: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (programVersionId: string) =>
      apiSend<SubmissionPackageView>('POST', `${cases}/${caseId}/packages`, { program_version_id: programVersionId }, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: invalidate,
  })
}

/** Audited export. Not idempotent on purpose: every pull is its own audit row, so no key. */
export function useExportPackage(caseId: string) {
  return useMutation({
    mutationFn: ({ packageId, reason }: { packageId: string; reason: string }) =>
      apiSend<PackageExportView>('POST', `${cases}/${caseId}/packages/${packageId}/export`, { reason }),
  })
}

// ── routing (PR 17b): one application, N partners ──

/**
 * Current routed programs with live per-partner readiness, latest package and active submission, plus the append-only history.
 * Readiness is recomputed server-side from documents, consent and requests, so the view is always refetched on mount.
 */
export function useRouting(caseId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.routing(caseId),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () => apiGet<CaseRoutingView>(`${cases}/${caseId}/routing`),
  })
}

/** Named command `addRoutingTargets`. Already-routed ids are no-ops server-side. */
export function useAddRoutingTargets(caseId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: AddRoutingTargetsBody) => apiSend<CaseRoutingView>('POST', `${cases}/${caseId}/routing/targets`, body),
    onSuccess: invalidate,
  })
}

/** Named command `removeRoutingTarget`; refused with 409 while the partner holds an active submission. */
export function useRemoveRoutingTarget(caseId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: ({ programVersionId, ...body }: RemoveRoutingTargetBody & { programVersionId: string }) =>
      apiSend<CaseRoutingView>('POST', `${cases}/${caseId}/routing/targets/${programVersionId}/remove`, body),
    onSuccess: invalidate,
  })
}

/** Named command `prepareRouting`: build + prepare per routed partner in one go. Never dispatches. */
export function usePrepareRouting(caseId: string, idempotencyKey: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: PrepareRoutingBody) =>
      apiSend<RoutingPrepareResponse>('POST', `${cases}/${caseId}/routing/prepare`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: invalidate,
  })
}

// ── submissions (PR 12) ──

export function useSubmissions(caseId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.submissions(caseId),
    enabled,
    queryFn: () => apiGet<LenderSubmissionListResponse>(`${cases}/${caseId}/submissions`),
  })
}

export function useSubmissionReadiness(caseId: string, submissionId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.submissionReadiness(caseId, submissionId),
    enabled,
    queryFn: () => apiGet<SubmissionReadinessView>(`${cases}/${caseId}/submissions/${submissionId}/readiness`),
  })
}

export function usePrepareSubmission(caseId: string, idempotencyKey: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: PrepareSubmissionBody) =>
      apiSend<LenderSubmissionView>('POST', `${cases}/${caseId}/submissions`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: invalidate,
  })
}

/** Named command `dispatchSubmission`: the only way a case reaches `lender_review`. */
export function useDispatchSubmission(caseId: string, submissionId: string, idempotencyKey: string) {
  const qc = useQueryClient()
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: DispatchSubmissionBody) =>
      apiSend<LenderSubmissionView>('POST', `${cases}/${caseId}/submissions/${submissionId}/dispatch`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: () => Promise.all([invalidate(), qc.invalidateQueries({ queryKey: lenderKeys.submissionReadiness(caseId, submissionId) })]),
  })
}

/** Mock adapter only; the server answers 409 `status_not_automated` for a manual partner. */
export function usePollSubmission(caseId: string, submissionId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: () => apiSend<LenderSubmissionView>('POST', `${cases}/${caseId}/submissions/${submissionId}/poll`),
    onSuccess: invalidate,
  })
}

export function useReconcileSubmission(caseId: string, submissionId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: ReconcileSubmissionBody) =>
      apiSend<LenderSubmissionView>('POST', `${cases}/${caseId}/submissions/${submissionId}/reconcile`, body),
    onSuccess: invalidate,
  })
}

export function useWithdrawSubmission(caseId: string, submissionId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: WithdrawSubmissionBody) =>
      apiSend<LenderSubmissionView>('POST', `${cases}/${caseId}/submissions/${submissionId}/withdraw`, body),
    onSuccess: invalidate,
  })
}

// ── decisions & offers (PR 13) ──

export function useDecisions(caseId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.decisions(caseId),
    enabled,
    queryFn: () => apiGet<LenderDecisionListResponse>(`${cases}/${caseId}/decisions`),
  })
}

export function useRecordDecision(caseId: string, submissionId: string, idempotencyKey: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: RecordDecisionBody) =>
      apiSend<RecordDecisionResponse>('POST', `${cases}/${caseId}/submissions/${submissionId}/decisions`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: invalidate,
  })
}

/** Tex projection: every offer, including those currently withheld from the buyer. */
export function useOffers(caseId: string, enabled = true) {
  return useQuery({
    queryKey: lenderKeys.offers(caseId),
    enabled,
    queryFn: () => apiGet<TexOffersResponse>(`${cases}/${caseId}/offers`),
  })
}

export function useRecordDisclosure(caseId: string, offerId: string) {
  const invalidate = useInvalidateLenderState(caseId)
  return useMutation({
    mutationFn: (body: RecordOfferDisclosureBody) => apiSend<OfferView>('POST', `${cases}/${caseId}/offers/${offerId}/disclosure`, body),
    onSuccess: invalidate,
  })
}

export function decisionsForSubmission(decisions: LenderDecisionView[] | undefined, submissionId: string): LenderDecisionView[] {
  return (decisions ?? []).filter((d) => d.submission_id === submissionId)
}
